/*
  计算偏心镜片
 * File: Decentered.js
 */

import { addToSphMap } from "@/lib/lens/usage";
import { getSiteConfiguration } from "@/src/utils/configUtils";
import { IAlert, IH5ShowDecenterAlert } from ".";
import { useCommonContext, useRateContext } from "@/src/provider";
import { ReactNode, useMemo } from "react";
import { IFormData } from "@/src/productDetails/Rx/RxForm";
import { useTranslation } from "@/src/i18n/client";
interface IDecentered extends IH5ShowDecenterAlert {}
export default class Decentered {
    frameDiagonally: number;
    frameBridgeWidth: number;
    usage?: string;
    usageDetail?: string;
    add: number;
    pd?: number;
    leftPd?: number;
    rightPd?: number;
    cate: number;
    leftEyeData: { sph?: number | null; cyl?: number | null };
    rightEyeData: { sph?: number | null; cyl?: number | null };
    constructor(
        prescriptionObj: IFormData | undefined,
        frameObj: Record<string, any> | undefined,
        usage: string | undefined,
        usageDetail: string | undefined,
    ) {
        this.frameDiagonally = frameObj?.dimensions_diagonal;
        this.frameBridgeWidth = frameObj?.dimensions_bridge;
        this.usage = usage;
        this.usageDetail = usageDetail;
        const { pd, left_pd, right_pd, os_sph, od_sph, os_cyl, od_cyl, add } =
            prescriptionObj!;

        this.add = this.transKeyToZero(add);
        if (pd) {
            this.pd = this.transKeyToZero(pd);
        } else if (left_pd && right_pd) {
            if (left_pd === right_pd) {
                this.pd = this.transKeyToZero(right_pd) * 2;
            } else {
                this.leftPd = this.transKeyToZero(left_pd) * 2;
                this.rightPd = this.transKeyToZero(right_pd) * 2;
            }
        } else {
            throw new Error("PD number required");
        }
        this.cate = frameObj?.master_categories_id;
        this.leftEyeData = { sph: os_sph as number, cyl: os_cyl as number };
        this.rightEyeData = { sph: od_sph as number, cyl: od_cyl as number };
    }

    get leftEye() {
        return this.transSphWithCyl(this.leftEyeData, this.add);
    }
    get rightEye() {
        return this.transSphWithCyl(this.rightEyeData, this.add);
    }
    get leftDia() {
        return this.caclLensDia(this.leftEye, this.usage);
    }
    get rightDia() {
        return this.caclLensDia(this.rightEye, this.usage);
    }
    // 取最小的
    get lensDia() {
        return this.leftDia > this.rightDia ? this.rightDia : this.leftDia;
    }
    //判断是否偏心
    get isDecentered() {
        const A = this.frameDiagonally;
        const B = this.frameBridgeWidth;
        const C = this.lensDia;
        let PD;
        if (this.pd) {
            PD = this.pd;
            return this.isDecen(A, B, C, PD);
        } else if (this.leftPd && this.rightPd) {
            const leftDecen = this.isDecen(A, B, C, this.leftPd);
            const rightDecen = this.isDecen(A, B, C, this.rightPd);
            let small;
            if (leftDecen.tooSmall || rightDecen.tooSmall) {
                small = true;
            } else if (!leftDecen.tooSmall && !rightDecen.tooSmall) {
                small = false;
            } else {
                small = "MIXED_CHOOSE";
            }
            return {
                isDecen: leftDecen.isDecen || rightDecen.isDecen,
                tooSmall: small,
            };
        } else {
            throw new Error("pd required!");
        }
    }
    isDecen(A: any, B: any, C: any, PD: any) {
        const min = 2 * A + B - C;
        const max = B + C;
        const tooSmall = PD - min < 0;
        if (PD < min || PD > max) {
            return { isDecen: true, tooSmall };
        } else {
            return { isDecen: false, tooSmall };
        }
    }
    caclLensDia(
        {
            sph,
            cyl,
        }: {
            sph: any;
            cyl: any;
        },
        usage?: string,
    ) {
        if (usage !== "singleVision") {
            return 68;
        } else if (sph > 0 && cyl > 0) {
            return 63;
        } else if (sph <= 0 && cyl <= 0) {
            return 68;
        } else if (sph > 0 && cyl <= 0) {
            return 63;
        } else if (sph <= 0 && cyl > 0) {
            const sphIsPositiveNum = sph + cyl > 0;
            const C = sphIsPositiveNum ? 63 : 68;
            return C;
        } else {
            return 68;
        }
    }
    /**
     *如若没有输入则相关为0
     *
     * @param {*} key
     * @returns
     */
    transKeyToZero(key: any) {
        if (!key) return -0;
        if (typeof key === "string") return parseFloat(key);
        return key;
    }
    /**
     *换算球径和散光
     *
     * @param {*} sph
     */
    transSphWithCyl(
        {
            sph,
            cyl,
        }: {
            sph?: number | null;
            cyl?: number | null;
        },
        add: number,
    ) {
        sph = this.transKeyToZero(sph);
        cyl = this.transKeyToZero(cyl);
        const usage = this.usageDetail;
        // 单焦add换算
        sph = add ? addToSphMap(sph!, add, usage) : sph;
        return { sph, cyl };
    }
}

const ShowDecenterAlert = ({
    rx,
    productInfo,
    usage,
    usageDetail,
    isDecenter,
    customer = true,
    alert,
    open,
    nextStep,
    id,
}: Partial<IH5ShowDecenterAlert> & {
    alert: (props: IAlert) => ReactNode;
    customer?: boolean;
}) => {
    const { host } = useCommonContext();
    const { t } = useTranslation("lens");

    const [getPrice] = useRateContext((state) => [state.getPrice]);
    const calcDecentered = new Decentered(rx, productInfo, usage, usageDetail);
    const isDecentered = calcDecentered.isDecentered;
    const { isDecen, tooSmall } = isDecentered;
    // 太阳镜，儿童镜都要判断，都要收费
    const message = useMemo(() => {
        if (isDecen && customer) {
            if (tooSmall && typeof tooSmall === "string") {
                return t("lens:MIXED_CHOOSE");
            } else if (tooSmall) {
                return `${t("lens:DECENTERED_TIPS")} ${getPrice(
                    getSiteConfiguration("specialLens", host),
                )}${t("lens:DECENTERED_TIPS2")}`;
            } else {
                return `${t("lens:DECENTERED_TIPS_TOO_BIG")}${getPrice(
                    getSiteConfiguration("specialLens", host),
                )}${t("lens:DECENTERED_TIPS_TOO_BIG2")}`;
            }
        }
    }, [customer, getPrice, host, isDecen, t, tooSmall]);
    return (
        <>
            {isDecen && customer ? (
                <>
                    {isDecenter?.()}
                    {alert({
                        message: message!,
                        open: open!,
                        nextStep,
                        usage,
                        usageDetail,
                        productInfo,
                        id,
                    })}
                </>
            ) : (
                nextStep?.()
            )}
        </>
    );
};

export { ShowDecenterAlert };
